/**
 * Slow-mover report — `/inventory/reports/slow-movers`.
 *
 * Like dead stock, `days` is REQUIRED — here it is the analysis window. The
 * denominators exclude zero-stock days server-side, which materially changes
 * average demand and days of cover, so the screen says so instead of letting
 * anyone re-derive the numbers by hand.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@erp/ui";

import { DataTable, textCell } from "@/components/data-table";
import type { SortState } from "@/components/data-table";
import { formatDateTime, formatNumber } from "@/lib/format";

import { REPORT_GROUP_BYS, type ReportGroupBy, type SlowMoverItem } from "../api";
import { useSlowMoversReport, useWarehouseOptions } from "../queries";
import { AmountText, QuantityText, RatioText } from "../components/decimal-text";
import { ALL, CategorySelect } from "../components/pickers";

const DAY_PRESETS = [30, 60, 90, 180] as const;
const MIN_DAYS = 1;
const MAX_DAYS = 730;

/** Sort fields the endpoint documents; anything else is not offered. */
const SORTABLE = new Set([
  "days_of_cover",
  "average_daily_demand",
  "issued_qty",
  "stock_value",
  "sku",
]);

function warehouseCodeOf(item: SlowMoverItem): string | undefined {
  return "warehouse_code" in item ? item.warehouse_code : undefined;
}

export interface SlowMoversPageProps {
  canReadCategories?: boolean;
  onOpenVariantStats?: (variantId: string) => void;
  pageLimit?: number;
}

export function SlowMoversPage({
  canReadCategories = false,
  onOpenVariantStats,
  pageLimit,
}: SlowMoversPageProps) {
  const { t, i18n } = useTranslation();

  const [daysInput, setDaysInput] = useState("90");
  const [groupBy, setGroupBy] = useState<ReportGroupBy>("warehouse");
  const [warehouseId, setWarehouseId] = useState<string>(ALL);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ key: "days_of_cover", dir: "desc" });

  const warehouses = useWarehouseOptions().data ?? [];

  const parsedDays = Number.parseInt(daysInput, 10);
  const daysValid =
    Number.isInteger(parsedDays) && parsedDays >= MIN_DAYS && parsedDays <= MAX_DAYS;

  const params = useMemo(
    () => ({
      days: daysValid ? parsedDays : MIN_DAYS,
      group_by: groupBy,
      ...(warehouseId === ALL ? {} : { warehouse_id: warehouseId }),
      ...(categoryId === null ? {} : { category_id: categoryId }),
      ...(sort === null ? {} : { sort }),
      ...(pageLimit === undefined ? {} : { limit: pageLimit }),
    }),
    [daysValid, parsedDays, groupBy, warehouseId, categoryId, sort, pageLimit],
  );

  const report = useSlowMoversReport(params, daysValid);
  const meta = report.meta;

  const columns = useMemo<ColumnDef<SlowMoverItem>[]>(
    () => [
      {
        accessorKey: "sku",
        id: "sku",
        header: t("inventory.common.sku", "SKU"),
        enableSorting: SORTABLE.has("sku"),
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        id: "warehouse",
        header: t("inventory.common.warehouse", "Warehouse"),
        enableSorting: false,
        cell: (ctx) => textCell(warehouseCodeOf(ctx.row.original) ?? ctx.row.original.branch_code),
      },
      {
        accessorKey: "on_hand_qty",
        id: "on_hand_qty",
        header: t("inventory.stock.columns.onHand", "On hand"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <QuantityText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "stock_value",
        id: "stock_value",
        header: t("inventory.common.value", "Value"),
        enableSorting: SORTABLE.has("stock_value"),
        meta: { numeric: true },
        cell: (ctx) => <AmountText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "issued_qty",
        id: "issued_qty",
        header: t("inventory.slowMovers.issued", "Issued in window"),
        enableSorting: SORTABLE.has("issued_qty"),
        meta: { numeric: true },
        cell: (ctx) => <QuantityText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "average_daily_demand",
        id: "average_daily_demand",
        header: t("inventory.slowMovers.avgDemand", "Avg daily demand"),
        enableSorting: SORTABLE.has("average_daily_demand"),
        meta: { numeric: true },
        cell: (ctx) => <RatioText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "active_stock_days",
        id: "active_stock_days",
        header: t("inventory.slowMovers.activeDays", "Days in stock"),
        enableSorting: false,
        meta: { numeric: true },
        // A whole-day count, not a wire decimal.
        cell: (ctx) => formatNumber(ctx.getValue<number>(), i18n.language),
      },
      {
        accessorKey: "days_of_cover",
        id: "days_of_cover",
        header: t("inventory.slowMovers.cover", "Days of cover"),
        enableSorting: SORTABLE.has("days_of_cover"),
        meta: { numeric: true },
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          // Omitted by the contract exactly when average demand is zero.
          if (value === undefined) {
            return (
              <Badge variant="warning" aria-label={t("inventory.slowMovers.noDemand", "No demand")}>
                ∞
              </Badge>
            );
          }
          return <RatioText value={value} fractionDigits={1} />;
        },
      },
      {
        id: "actions",
        header: t("inventory.common.actions", "Actions"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) =>
          onOpenVariantStats === undefined ? null : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenVariantStats(ctx.row.original.variant_id)}
            >
              {t("inventory.slowMovers.openStats", "Cover detail")}
            </Button>
          ),
      },
    ],
    [t, i18n.language, onOpenVariantStats],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">{t("inventory.slowMovers.title", "Slow movers")}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "inventory.slowMovers.subtitle",
            "Demand and cover per position over an analysis window you choose.",
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="slow-movers-days">
            {t("inventory.slowMovers.days", "Analysis window (days)")}
          </Label>
          <Input
            id="slow-movers-days"
            dir="ltr"
            inputMode="numeric"
            value={daysInput}
            aria-invalid={!daysValid}
            onChange={(event) => setDaysInput(event.target.value)}
          />
          {daysValid ? (
            <div className="flex flex-wrap gap-1">
              {DAY_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  size="sm"
                  variant={parsedDays === preset ? "default" : "outline"}
                  className="h-7 px-2 text-xs"
                  onClick={() => setDaysInput(String(preset))}
                >
                  {formatNumber(preset, i18n.language)}
                </Button>
              ))}
            </div>
          ) : (
            <p role="alert" className="text-sm text-destructive">
              {t(
                "inventory.slowMovers.daysInvalid",
                "Enter a whole number of days between 1 and 730 — this report cannot run without it.",
              )}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="slow-movers-group">{t("inventory.reports.groupBy", "Grouped by")}</Label>
          <Select value={groupBy} onValueChange={(value) => setGroupBy(value as ReportGroupBy)}>
            <SelectTrigger id="slow-movers-group">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORT_GROUP_BYS.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`inventory.reports.group.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="slow-movers-warehouse">
            {t("inventory.common.warehouse", "Warehouse")}
          </Label>
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger id="slow-movers-warehouse">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} — {warehouse.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canReadCategories ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="slow-movers-category">
              {t("inventory.common.category", "Category")}
            </Label>
            <CategorySelect
              id="slow-movers-category"
              value={categoryId}
              canRead={canReadCategories}
              onChange={setCategoryId}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 p-3 text-xs">
        {meta !== null ? (
          <>
            <span className="text-muted-foreground">
              {t("inventory.reports.asOf", "As of {{when}}", {
                when: formatDateTime(meta.as_of, i18n.language),
              })}
            </span>
            {meta.analysis_window_days !== undefined ? (
              <Badge variant="outline">
                {t("inventory.slowMovers.window", "{{days}} day window", {
                  days: meta.analysis_window_days,
                })}
              </Badge>
            ) : null}
          </>
        ) : null}
        {/* Nested provider: the shell supplies one, isolated renders need their own. */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help underline decoration-dotted">
                {t("inventory.slowMovers.zeroStockNote", "Zero-stock days are excluded")}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {t(
                "inventory.slowMovers.zeroStockTooltip",
                "Average demand and days of cover divide by days the item actually had stock, so a stock-out does not flatter the numbers.",
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <DataTable
        columns={columns}
        query={report.table}
        getRowId={(row) => `${row.variant_id}:${warehouseCodeOf(row) ?? row.branch_id}`}
        sort={sort}
        onSortChange={setSort}
        storageKey="inventory.reports.slow-movers.columns"
        emptyMessage={t("inventory.slowMovers.empty", "Nothing qualifies in this window.")}
        errorMessage={t("inventory.slowMovers.loadError", "The report could not be loaded.")}
      />
    </div>
  );
}
